// Topbar Notifications Dropdown component (WP-04).
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { useNotifications, type NotificationDoc } from '../../lib/notifications';
import {
  BellIcon,
  CheckIcon,
  ClockIcon,
  UserIcon,
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
} from '../icons';

function fmtRelativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function NotificationIcon({ type }: { type: string }) {
  switch (type) {
    case 'followup_due':
      return <ClockIcon size={16} className="text-amber-500 shrink-0" />;
    case 'assignment':
      return <UserIcon size={16} className="text-primary shrink-0" />;
    case 'broadcast_completed':
    case 'template_approved':
      return <CheckCircleIcon size={16} className="text-emerald-500 shrink-0" />;
    case 'broadcast_failed':
    case 'template_rejected':
      return <AlertTriangleIcon size={16} className="text-rose-500 shrink-0" />;
    default:
      return <AlertCircleIcon size={16} className="text-slate-400 shrink-0" />;
  }
}

export default function NotificationsDropdown() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { items, unreadCount, markRead, markAllRead } = useNotifications();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleItemClick = (item: NotificationDoc) => {
    void markRead(item.id);
    setOpen(false);
    if (item.link) {
      navigate(item.link);
    } else if (item.conversationId) {
      navigate('/inbox');
    }
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('topbar.notifications')}
        aria-expanded={open}
        className="relative grid h-9 w-9 place-items-center rounded-full border border-line bg-slate-50 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        <BellIcon size={17} />
        {unreadCount > 0 && (
          <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[9px] font-bold text-white shadow-sm">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute end-0 top-full z-50 mt-2 w-80 sm:w-96 overflow-hidden rounded-card border border-line bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between border-b border-line px-4 py-3 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-ink dark:text-slate-100">
                {t('notif.title') || 'Notifications'}
              </span>
              {unreadCount > 0 && (
                <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-bold text-brand dark:bg-brand-950/40 dark:text-brand-tint">
                  {unreadCount} {t('notif.new') || 'new'}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline dark:text-brand-tint"
              >
                <CheckIcon size={14} />
                <span>{t('notif.markAllRead') || 'Mark all as read'}</span>
              </button>
            )}
          </div>

          <div className="max-h-[380px] overflow-y-auto divide-y divide-line dark:divide-slate-800">
            {items.length === 0 ? (
              <div className="p-8 text-center text-xs text-ink-3 dark:text-slate-500">
                {t('notif.empty') || 'No notifications yet'}
              </div>
            ) : (
              items.map((item) => {
                const isUnread = !item.readAt;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className={cn(
                      'flex w-full items-start gap-3 p-3.5 text-start transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60',
                      isUnread && 'bg-slate-50/70 dark:bg-slate-800/40',
                    )}
                  >
                    <div className="mt-0.5">
                      <NotificationIcon type={item.type} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className={cn('truncate text-xs text-ink dark:text-slate-200', isUnread ? 'font-bold' : 'font-medium')}>
                          {item.title}
                        </span>
                        <span className="shrink-0 text-[10px] text-ink-4 dark:text-slate-500">
                          {fmtRelativeTime(item.createdAt)}
                        </span>
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-xs text-ink-3 dark:text-slate-400">
                        {item.body}
                      </p>
                    </div>
                    {isUnread && (
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand" aria-hidden />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
